import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#bindAccountOrdersLink();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#unbindAccountOrdersLink();
  }

  /** @type {HTMLElement | null} */
  #accountButton = null;

  /** @param {Event} event */
  #openAccountOrders = (event) => {
    const ordersUrl = this.#accountButton?.dataset.accountOrdersUrl;
    if (!ordersUrl) return;

    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    window.open(ordersUrl, '_blank', 'noopener,noreferrer');
  };

  #bindAccountOrdersLink() {
    this.#accountButton = this.querySelector('.account-button[data-account-orders-url]');
    if (!this.#accountButton) return;

    this.#accountButton.addEventListener('click', this.#openAccountOrders, true);
    this.#accountButton.addEventListener('keydown', this.#openAccountOrders, true);
  }

  #unbindAccountOrdersLink() {
    if (!this.#accountButton) return;

    this.#accountButton.removeEventListener('click', this.#openAccountOrders, true);
    this.#accountButton.removeEventListener('keydown', this.#openAccountOrders, true);
    this.#accountButton = null;
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
